import { Link } from "react-router-dom";
import { ArrowLeft, Wallet, CheckCircle2, AlertCircle } from "lucide-react";
import usePayouts from "../components/payouts/usePayouts";
import PayoutKpis from "../components/payouts/PayoutKpis";
import PayoutFilters from "../components/payouts/PayoutFilters";
import PayoutBatchForm from "../components/payouts/PayoutBatchForm";
import PayoutTable from "../components/payouts/PayoutTable";
import { downloadCsv } from "../components/payouts/payoutCsv";

function PageHeader() {
  return (
    <>
      <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-[#978D78] hover:text-[#0F1829] mb-3">
        <ArrowLeft size={13} /> Voltar ao dashboard
      </Link>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white"><Wallet size={22} /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Pagamentos de Comissões</h1>
          <p className="text-sm text-[#4B5563]">Processa pagamentos em lote aos consultores com valor por pagar.</p>
        </div>
      </div>
    </>
  );
}

function ResultBanners({ err, result }) {
  return (
    <>
      {err && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700 rounded flex items-start gap-2" data-testid="payouts-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" /> {err}
        </div>
      )}
      {result && (
        <div className={`mb-3 px-3 py-2 border text-sm rounded flex items-start gap-2 ${
          result.failed === 0 ? "bg-green-50 border-green-200 text-green-700" : "bg-amber-50 border-amber-200 text-amber-800"
        }`} data-testid="payouts-result">
          <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
          <div>
            <strong>{result.success}/{result.total}</strong> pagamentos registados com sucesso.
            {result.failed > 0 && <span className="ml-2">({result.failed} falhas — ver log de auditoria)</span>}
          </div>
        </div>
      )}
    </>
  );
}

function Pager({ total, page, pageSize, setPage }) {
  if (total <= pageSize) return null;
  const totalPages = Math.ceil(total / pageSize);
  return (
    <div className="flex items-center justify-center gap-2 mt-3 text-sm">
      <span className="text-xs text-[#6B7280] mr-2">
        {total.toLocaleString("pt-PT")} resultados · pág {page} / {totalPages}
      </span>
      <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
        className="px-2 py-1 text-xs border border-[#E6E1D6] rounded disabled:opacity-30 hover:bg-[#F5F2EB]">← Anterior</button>
      <button onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}
        className="px-2 py-1 text-xs border border-[#E6E1D6] rounded disabled:opacity-30 hover:bg-[#F5F2EB]">Seguinte →</button>
    </div>
  );
}

export default function AdminCommissionPayouts() {
  const p = usePayouts();
  return (
    <div data-testid="admin-commission-payouts" className="max-w-7xl">
      <PageHeader />

      <PayoutKpis
        total={p.total}
        sumTotal={p.sumTotal}
        onlyAvailable={p.onlyAvailable}
        selectedCount={p.selected.size}
        totalSelected={p.totalSelected}
      />

      <PayoutFilters
        minValue={p.minValue} setMinValue={p.setMinValue}
        tipoCliente={p.tipoCliente} setTipoCliente={p.setTipoCliente}
        onlyAvailable={p.onlyAvailable} setOnlyAvailable={p.setOnlyAvailable}
        setPage={p.setPage}
        onRefresh={p.load}
        onExport={() => downloadCsv(p.rows)}
        loading={p.loading}
        hasRows={p.rows.length > 0}
      />

      <PayoutBatchForm
        data={p.data} setData={p.setData}
        tipoPag={p.tipoPag} setTipoPag={p.setTipoPag} paymentTypes={p.paymentTypes}
        obs={p.obs} setObs={p.setObs}
        onSubmit={p.submit}
        submitting={p.submitting}
        selectedCount={p.selected.size}
      />

      <ResultBanners err={p.err} result={p.result} />

      <PayoutTable
        rows={p.rows}
        loading={p.loading}
        selected={p.selected}
        overrides={p.overrides}
        targetCol={p.targetCol}
        toggle={p.toggle}
        toggleAll={p.toggleAll}
        setOverride={p.setOverride}
      />

      <Pager total={p.total} page={p.page} pageSize={p.pageSize} setPage={p.setPage} />
    </div>
  );
}
